import { useCallback, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { T2SEngineAtom, currentSpeakerAtom, volumeAtom } from "~/Atom";
import { audioManager, playVoice, type PlayVoiceParams } from "~/SoundUtils";

/**
 * Reception pages dedicated voice playback hook with cooldown lock.
 */
type UseReceptionPlayVoiceWithCooldownOptions = {
  cooldownMs?: number;
  volumeScale?: number;
  endExpression?: string;
};

/**
 * Reception ページ向けのクールダウン付き音声再生フック。
 *
 * このフックは `T2SEngineAtom`, `currentSpeakerAtom`, `volumeAtom` を参照し、
 *
 * @param {Object} [options]
 * @param {number} [options.cooldownMs=2000] - 再生後のクールダウン時間（ミリ秒）
 * @param {number} [options.volumeScale=1] - 再生音量の倍率
 * @param {string} [options.endExpression="neutral"] - 再生終了時にemitterで通知する表情。
 *                "bow" を指定すると発話終了後にお辞儀モーションが再生される。
 * @returns {{ playWithCooldown: (inputText: string) => Promise<boolean> }} - 指定テキストをクールダウン付きで再生し、再生が行われた場合に `true`、再生がスキップされた場合は `false` を返します。
 *
 * @remarks
 * - 空文字列（trim後）が渡された場合は再生を行いません。
 * - `volume <= 0` のときは再生を行いません。
 * - コンポーネントのアンマウント時にタイマーをクリアします。
 */

export const usePlayVoiceWithCooldown = ({
  cooldownMs = 2000,
  volumeScale = 1,
  endExpression = "neutral",
}: UseReceptionPlayVoiceWithCooldownOptions = {}) => {
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [volume] = useAtom(volumeAtom);

  // Refs to read latest T2SEngine without adding it to useCallback deps
  const T2SEngineRef = useRef(T2SEngine);

  useEffect(() => {
    T2SEngineRef.current = T2SEngine;
  }, [T2SEngine]);

  const isPlaybackLockedRef = useRef(false);
  const playbackUnlockTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (playbackUnlockTimeoutRef.current !== null) {
        window.clearTimeout(playbackUnlockTimeoutRef.current);
        playbackUnlockTimeoutRef.current = null;
      }
      audioManager.stopCurrentAudio();
      isPlaybackLockedRef.current = false;
    };
  }, []);

  const playWithCapturedState = useCallback(
    async (
      inputText: string,
      engine: string,
      speaker: { Id?: number; Name?: string; Style?: string } | null | undefined
    ): Promise<boolean> => {
      const finalSpeaker = {
        Id: speaker?.Id ?? 0,
        Name: speaker?.Name ?? "",
        Style: speaker?.Style ?? "",
      };

      const params: PlayVoiceParams = {
        inputText,
        T2SEngine: engine,
        currentSpeaker: finalSpeaker,
        speed: 1.0,
        pitch: 0.0,
        intonation: 1.0,
        volume: volume * volumeScale,
        talkingMode: 9,
        endExpression,
      };

      try {
        await playVoice(params);
      } finally {
        if (playbackUnlockTimeoutRef.current !== null) {
          window.clearTimeout(playbackUnlockTimeoutRef.current);
        }
        playbackUnlockTimeoutRef.current = window.setTimeout(() => {
          isPlaybackLockedRef.current = false;
          playbackUnlockTimeoutRef.current = null;
        }, cooldownMs);
      }

      return true;
    },
    [volume, volumeScale, cooldownMs, endExpression]
  );

  const playWithCooldown = useCallback(
    async (inputText: string): Promise<boolean> => {
      if (isPlaybackLockedRef.current) {
        return false;
      }
      if (!inputText.trim()) return false;
      if (volume <= 0) return false;

      // Capture snapshot values at call time and lock to them.
      const engineSnapshot = T2SEngineRef.current;

      isPlaybackLockedRef.current = true;

      return await playWithCapturedState(inputText, engineSnapshot, currentSpeaker);
    },
    [volume, playWithCapturedState, currentSpeaker]
  );

  return {
    playWithCooldown,
  };
};
